import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';

interface ShippingSettings {
  baseShippingFee: number;
  freeShippingThreshold: number | null;
  progressBarEnabled: boolean;
  progressBarTextTemplateSv: string;
  progressBarTextTemplateEn: string;
  progressBarTextTemplateAr: string;
  freeShippingAchievedSv: string;
  freeShippingAchievedEn: string;
  freeShippingAchievedAr: string;
}

const DEFAULT_SETTINGS: ShippingSettings = {
  baseShippingFee: 59,
  freeShippingThreshold: null,
  progressBarEnabled: false,
  progressBarTextTemplateSv: 'Handla för {remaining} kr till för fri frakt',
  progressBarTextTemplateEn: 'Spend {remaining} SEK more for free shipping',
  progressBarTextTemplateAr: 'أنفق {remaining} كرونة سويدية إضافية للحصول على شحن مجاني',
  freeShippingAchievedSv: 'Du har fri frakt!',
  freeShippingAchievedEn: 'You qualify for free shipping!',
  freeShippingAchievedAr: 'أنت مؤهل للشحن المجاني!',
};

export function useShippingSettings() {
  const [settings, setSettings] = useState<ShippingSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('shipping_settings')
        .select('base_shipping_fee, free_shipping_threshold, progress_bar_enabled, progress_bar_text_template_sv, progress_bar_text_template_en, progress_bar_text_template_ar, free_shipping_achieved_sv, free_shipping_achieved_en, free_shipping_achieved_ar')
        .single();

      if (error) {
        console.error('Failed to fetch shipping settings:', error);
        return;
      }

      if (data) {
        setSettings({
          baseShippingFee: data.base_shipping_fee,
          freeShippingThreshold: data.free_shipping_threshold,
          progressBarEnabled: data.progress_bar_enabled,
          progressBarTextTemplateSv: data.progress_bar_text_template_sv || DEFAULT_SETTINGS.progressBarTextTemplateSv,
          progressBarTextTemplateEn: data.progress_bar_text_template_en || DEFAULT_SETTINGS.progressBarTextTemplateEn,
          progressBarTextTemplateAr: data.progress_bar_text_template_ar || DEFAULT_SETTINGS.progressBarTextTemplateAr,
          freeShippingAchievedSv: data.free_shipping_achieved_sv || DEFAULT_SETTINGS.freeShippingAchievedSv,
          freeShippingAchievedEn: data.free_shipping_achieved_en || DEFAULT_SETTINGS.freeShippingAchievedEn,
          freeShippingAchievedAr: data.free_shipping_achieved_ar || DEFAULT_SETTINGS.freeShippingAchievedAr,
        });
      }
    } catch (error) {
      console.error('Error fetching shipping settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateShipping = (subtotal: number): number => {
    if (settings.freeShippingThreshold !== null && subtotal >= settings.freeShippingThreshold) {
      return 0;
    }
    return settings.baseShippingFee;
  };

  const getProgressBarText = (): string => {
    switch (language) {
      case 'ar': return settings.progressBarTextTemplateAr;
      case 'en': return settings.progressBarTextTemplateEn;
      default: return settings.progressBarTextTemplateSv;
    }
  };

  const getFreeShippingAchievedText = (): string => {
    switch (language) {
      case 'ar': return settings.freeShippingAchievedAr;
      case 'en': return settings.freeShippingAchievedEn;
      default: return settings.freeShippingAchievedSv;
    }
  };

  const getProgressBarData = (subtotal: number) => {
    if (!settings.progressBarEnabled || settings.freeShippingThreshold === null) {
      return null;
    }

    if (subtotal >= settings.freeShippingThreshold) {
      return {
        progress: 100,
        remaining: 0,
        text: getFreeShippingAchievedText(),
        isFreeShipping: true,
      };
    }

    const remaining = settings.freeShippingThreshold - subtotal;
    const progress = (subtotal / settings.freeShippingThreshold) * 100;

    return {
      progress,
      remaining,
      text: getProgressBarText().replace('{remaining}', remaining.toFixed(0)),
      isFreeShipping: false,
    };
  };

  return {
    settings,
    loading,
    calculateShipping,
    getProgressBarData,
  };
}